import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS, PROFILE } from "@/data/content";
import { TID } from "@/constants/testIds";

const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const Navigation = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [open]);

    const initials = PROFILE.name
        .split(" ")
        .map((s) => s[0])
        .join("");

    return (
        <>
            <motion.header
                initial={{ y: -30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                className={`fixed left-0 right-0 top-0 z-40 transition-colors duration-500 ${
                    scrolled
                        ? "backdrop-blur-xl bg-[#050505]/70 border-b border-white/[.06]"
                        : "bg-transparent"
                }`}
            >
                <div className="container-x flex h-16 items-center justify-between md:h-20">
                    <button
                        data-testid={TID.navLogo}
                        onClick={() => scrollTo("top")}
                        className="group flex items-center gap-3"
                        aria-label="Go to top"
                    >
                        <span
                            className="grid h-9 w-9 place-items-center rounded-xl border border-white/10 bg-white/[.03] text-[13px] font-semibold text-white transition-colors group-hover:border-white/20"
                            style={{
                                fontFamily: "'General Sans', sans-serif"
                            }}
                        >
                            {initials}
                        </span>
                        <span className="hidden text-sm font-medium tracking-tight text-white sm:inline">
                            {PROFILE.name}
                        </span>
                    </button>

                    <nav className="hidden items-center gap-8 md:flex">
                        {NAV_LINKS.map((l) => (
                            <button
                                key={l.id}
                                data-testid={TID.navLink(l.id)}
                                onClick={() => scrollTo(l.id)}
                                className="text-[13px] font-medium text-brand-muted transition-colors hover:text-white"
                            >
                                {l.label}
                            </button>
                        ))}
                    </nav>

                    <div className="flex items-center gap-2">
                        <button
                            data-testid={TID.navContactCta}
                            onClick={() => scrollTo("contact")}
                            className="hidden h-10 items-center gap-2 rounded-full border border-white/10 bg-white/[.03] px-4 text-[13px] font-medium text-white transition-all hover:border-white/25 hover:bg-white/[.06] md:inline-flex"
                        >
                            <span className="relative flex h-2 w-2">
                                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
                                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                            </span>
                            Available for work
                        </button>
                        <button
                            data-testid={TID.navMobileToggle}
                            aria-label="Open menu"
                            onClick={() => setOpen(true)}
                            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/[.03] text-white md:hidden"
                        >
                            <Menu className="h-5 w-5" />
                        </button>
                    </div>
                </div>
            </motion.header>

            {/* Mobile menu */}
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md md:hidden"
                        onClick={() => setOpen(false)}
                    >
                        <motion.div
                            initial={{ y: -20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            exit={{ y: -20, opacity: 0 }}
                            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                            className="absolute left-4 right-4 top-4 rounded-3xl border border-white/[.08] bg-[#0a0a0a] p-6"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="mb-6 flex items-center justify-between">
                                <span
                                    className="text-sm font-medium text-white"
                                    style={{ fontFamily: "'General Sans', sans-serif" }}
                                >
                                    {PROFILE.name}
                                </span>
                                <button
                                    data-testid={TID.navMobileClose}
                                    aria-label="Close menu"
                                    onClick={() => setOpen(false)}
                                    className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/[.03] text-white"
                                >
                                    <X className="h-5 w-5" />
                                </button>
                            </div>
                            <nav className="flex flex-col">
                                {NAV_LINKS.map((l) => (
                                    <button
                                        key={l.id}
                                        data-testid={TID.navMobileLink(l.id)}
                                        onClick={() => {
                                            scrollTo(l.id);
                                            setOpen(false);
                                        }}
                                        className="border-b border-white/[.06] py-4 text-left text-lg font-medium text-white transition-colors hover:text-brand-primary"
                                    >
                                        {l.label}
                                    </button>
                                ))}
                            </nav>
                            <button
                                onClick={() => {
                                    scrollTo("contact");
                                    setOpen(false);
                                }}
                                className="btn-primary mt-6 w-full"
                            >
                                Get in touch
                            </button>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default Navigation;
